import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Users, Zap, RefreshCw, TrendingUp, Clock, Target, UserPlus
} from "lucide-react";
import { useLeadAutoRouting } from "@/hooks/useLeadAutoRouting";
import { useLeads } from "@/hooks/useLeads";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

export function LeadAutoRoutingPanel() {
  const { calculateAgentScores, autoRouteUnassignedLeads, hasTeamMembers, isLoadingTeam } = useLeadAutoRouting();
  const { data: leads } = useLeads();
  const [isCalculating, setIsCalculating] = useState(false);

  const unassignedLeads = leads?.filter(l => !l.assigned_to_user_id) || [];

  const { data: agentScores, isLoading, refetch } = useQuery({
    queryKey: ['agent-routing-scores'],
    queryFn: async () => {
      setIsCalculating(true);
      const scores = await calculateAgentScores();
      setIsCalculating(false);
      return scores;
    },
    enabled: hasTeamMembers,
  });

  const handleRefresh = () => {
    refetch();
  };

  const handleRouteLeads = () => {
    if (!hasTeamMembers) {
      return;
    }
    autoRouteUnassignedLeads.mutate();
  };

  const isButtonDisabled = autoRouteUnassignedLeads.isPending || unassignedLeads.length === 0 || !hasTeamMembers || isLoadingTeam;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>AI Lead Routing</CardTitle>
              <CardDescription>
                Auto-assign leads based on performance, workload, and fit
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={!hasTeamMembers}>
              <RefreshCw className={cn("h-4 w-4 mr-2", isCalculating && "animate-spin")} />
              Refresh
            </Button>
            <Button 
              size="sm" 
              onClick={handleRouteLeads}
              disabled={isButtonDisabled}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Route {unassignedLeads.length} Leads
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Unassigned leads alert */}
        {unassignedLeads.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <Users className="h-4 w-4" />
              <span className="font-medium">{unassignedLeads.length} unassigned leads</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Click "Route Leads" to automatically assign these to the best-fit agents
            </p>
          </div>
        )}

        {/* Agent scores table */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-muted-foreground">Agent Routing Scores</div>
          
          {isLoadingTeam ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : !hasTeamMembers ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No team members found</p>
              <p className="text-sm mt-1">Add team members in Settings to enable auto-routing</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : agentScores && agentScores.length > 0 ? (
            <div className="space-y-2">
              {agentScores.map((agent, index) => (
                <div 
                  key={agent.user_id}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border",
                    index === 0 && "border-primary bg-primary/5"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                      index === 0 ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}>
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {agent.name}
                        {index === 0 && <Badge variant="default" className="text-xs">Best Match</Badge>}
                      </div>
                      <div className="text-sm text-muted-foreground">{agent.email}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-sm">
                        <Target className="h-3 w-3 text-muted-foreground" />
                        <span>{agent.win_rate}%</span>
                      </div>
                      <div className="text-xs text-muted-foreground">Win Rate</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-sm">
                        <Users className="h-3 w-3 text-muted-foreground" />
                        <span>{agent.current_leads}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">Active Leads</div>
                    </div>
                    
                    <div className="w-24">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">Score</span>
                        <span className="text-sm font-medium">{agent.score}</span>
                      </div>
                      <Progress value={agent.score} className="h-1.5" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No team members available for routing</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
